import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit2, Power } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
    getAllSupportTicketTags,
    createSupportTicketTag,
    updateSupportTicketTag,
    toggleSupportTicketTagStatus,
    seedDefaultSupportTicketTags,
} from '../../utils/api';
import toast from 'react-hot-toast';

const PRESET_COLORS = [
    '#3B82F6', // Blue
    '#8B5CF6', // Purple
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EC4899', // Pink
    '#6366F1', // Indigo
    '#14B8A6', // Teal
    '#F97316', // Orange
    '#84CC16', // Lime
];

export default function TagManager() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTag, setEditingTag] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [label, setLabel] = useState('');
    const [color, setColor] = useState(PRESET_COLORS[0]);
    const [description, setDescription] = useState('');

    // Fetch all tags
    const { data: tagsData, isLoading } = useQuery({
        queryKey: ['support-ticket-tags-all'],
        queryFn: getAllSupportTicketTags,
    });

    const tags = tagsData?.data || [];

    // Create tag mutation
    const createMutation = useMutation({
        mutationFn: () => createSupportTicketTag({ name, label, color, description }),
        onSuccess: () => {
            toast.success('Tag created');
            queryClient.invalidateQueries(['support-ticket-tags-all']);
            queryClient.invalidateQueries(['support-ticket-tags']);
            closeCreateModal();
        },
        onError: (error) => toast.error(error.message),
    });

    // Update tag mutation
    const updateMutation = useMutation({
        mutationFn: () => updateSupportTicketTag(editingTag._id, { label, color, description }),
        onSuccess: () => {
            toast.success('Tag updated');
            queryClient.invalidateQueries(['support-ticket-tags-all']);
            queryClient.invalidateQueries(['support-ticket-tags']);
            closeEditModal();
        },
        onError: (error) => toast.error(error.message),
    });

    // Toggle status mutation
    const toggleMutation = useMutation({
        mutationFn: ({ tagId, isActive }) => toggleSupportTicketTagStatus(tagId, isActive),
        onSuccess: () => {
            queryClient.invalidateQueries(['support-ticket-tags-all']);
            queryClient.invalidateQueries(['support-ticket-tags']);
        },
        onError: (error) => toast.error(error.message),
    });

    // Seed default tags mutation
    const seedMutation = useMutation({
        mutationFn: seedDefaultSupportTicketTags,
        onSuccess: (data) => {
            const created = data?.data?.created || [];
            if (created.length > 0) {
                toast.success(`Created ${created.length} default tags`);
            } else {
                toast.success('Default tags already exist');
            }
            queryClient.invalidateQueries(['support-ticket-tags-all']);
            queryClient.invalidateQueries(['support-ticket-tags']);
        },
        onError: (error) => toast.error(error.message),
    });

    const openCreateModal = () => {
        setName('');
        setLabel('');
        setColor(PRESET_COLORS[0]);
        setDescription('');
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setName('');
        setLabel('');
        setColor(PRESET_COLORS[0]);
        setDescription('');
    };

    const openEditModal = (tag) => {
        setEditingTag(tag);
        setLabel(tag.label);
        setColor(tag.color);
        setDescription(tag.description || '');
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingTag(null);
        setLabel('');
        setColor(PRESET_COLORS[0]);
        setDescription('');
    };

    const handleToggleStatus = (tag) => {
        toggleMutation.mutate({ tagId: tag._id, isActive: !tag.isActive });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate('/support')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold">Tag Manager</h1>
                        <p className="text-muted-light dark:text-muted-dark">
                            Manage support ticket tags
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {tags.length === 0 && (
                        <Button
                            variant="secondary"
                            onClick={() => seedMutation.mutate()}
                            loading={seedMutation.isPending}
                        >
                            Seed Default Tags
                        </Button>
                    )}
                    <Button onClick={openCreateModal}>
                        <Plus size={18} />
                        Create Tag
                    </Button>
                </div>
            </div>

            {/* Tags Grid */}
            <Card>
                {tags.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-muted-light dark:text-muted-dark mb-4">
                            No tags yet. Create your first tag or seed the default tags.
                        </p>
                        <Button
                            variant="secondary"
                            onClick={() => seedMutation.mutate()}
                            loading={seedMutation.isPending}
                        >
                            Seed Default Tags
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tags.map((tag) => (
                            <div
                                key={tag._id}
                                className={`p-4 rounded-lg border ${tag.isActive
                                        ? 'border-border-light dark:border-border-dark'
                                        : 'border-dashed border-muted-light dark:border-muted-dark opacity-50'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: tag.color }}
                                        />
                                        <span className="font-medium">{tag.label}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => openEditModal(tag)}
                                            className="p-1.5 rounded hover:bg-accent-light dark:hover:bg-accent-dark"
                                            title="Edit"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(tag)}
                                            className={`p-1.5 rounded hover:bg-accent-light dark:hover:bg-accent-dark ${tag.isActive ? 'text-green' : 'text-muted-light dark:text-muted-dark'
                                                }`}
                                            title={tag.isActive ? 'Disable' : 'Enable'}
                                        >
                                            <Power size={14} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-xs text-muted-light dark:text-muted-dark mb-1">
                                    ID: {tag.name}
                                </p>
                                {tag.description && (
                                    <p className="text-sm text-muted-light dark:text-muted-dark">
                                        {tag.description}
                                    </p>
                                )}
                                {!tag.isActive && (
                                    <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-muted-light/20 rounded">
                                        Disabled
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Create Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={closeCreateModal}
                title="Create Tag"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeCreateModal}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => createMutation.mutate()}
                            loading={createMutation.isPending}
                            disabled={!name.trim() || !label.trim()}
                        >
                            Create
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Name (ID)"
                        value={name}
                        onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                        placeholder="e.g., engineering"
                        required
                    />
                    <Input
                        label="Label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g., Engineering"
                        required
                    />
                    <div>
                        <label className="block text-sm font-medium mb-2">Color</label>
                        <div className="flex flex-wrap gap-2">
                            {PRESET_COLORS.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={`w-8 h-8 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-blue scale-110' : ''
                                        }`}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                    <Input
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Brief description of when to use this tag"
                    />
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={closeEditModal}
                title="Edit Tag"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeEditModal}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => updateMutation.mutate()}
                            loading={updateMutation.isPending}
                            disabled={!label.trim()}
                        >
                            Save
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">Name (ID)</label>
                        <p className="text-muted-light dark:text-muted-dark bg-accent-light dark:bg-accent-dark px-3 py-2 rounded">
                            {editingTag?.name}
                        </p>
                        <p className="text-xs text-muted-light dark:text-muted-dark mt-1">
                            Tag ID cannot be changed after creation
                        </p>
                    </div>
                    <Input
                        label="Label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="e.g., Engineering"
                        required
                    />
                    <div>
                        <label className="block text-sm font-medium mb-2">Color</label>
                        <div className="flex flex-wrap gap-2">
                            {PRESET_COLORS.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={`w-8 h-8 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-blue scale-110' : ''
                                        }`}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                    <Input
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Brief description of when to use this tag"
                    />
                </div>
            </Modal>
        </div>
    );
}

